import { FIELD_LONG_TEXT_MAX, FIELD_TEXT_MAX } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Checkbox, Field, Input, Select, Textarea } from "@sagot/ui";
import type { ReactNode } from "react";
import { statusLabelOf } from "../organisations/ticket-parts";
import { AssetPicker } from "./asset-picker";
import { isChoiceList, isFieldVisible, type FieldValue, type FormField, type NewTicketDraft, type Problems } from "./new-ticket-form";

interface FieldsProps {
  fields: readonly FormField[];
  values: NewTicketDraft["fields"];
  problems: Problems;
  language: string;
  /** The requester's organisation: the equipment picker offers only its assets. */
  organisationId?: string | null;
  onChange: (key: string, value: FieldValue) => void;
}

interface Choice {
  value: string;
  label: string;
}

/** Options of a dropdown or multi-select, in the viewer's language (the definition stores `{ value, label: { en, fil } }`). */
function optionsOf(form: FormField, language: string): Choice[] {
  return (form.definition?.options ?? []).flatMap((option) => {
    const value = option.value;
    if (typeof value !== "string") return [];
    const label = option.label;
    const text = isRecord(label) ? statusLabelOf(label, language) : undefined;
    return [{ value, label: text ?? value }];
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function bound(form: FormField, key: string): number | undefined {
  const value = form.definition?.bounds[key];
  return typeof value === "number" ? value : undefined;
}

export function labelOf(form: FormField, language: string): string {
  const label = form.field.label;
  return (language === "fil" ? label.fil : undefined) ?? label.en;
}

export function Control({ form, value, language, label, organisationId = null, onChange }: { form: FormField; value: FieldValue | undefined; language: string; label: string; organisationId?: string | null; onChange: (value: FieldValue) => void }): ReactNode {
  const text = typeof value === "string" ? value : "";
  const required = form.field.staff_required && form.field.required_on === "create";
  switch (form.field.type) {
    case "long_text":
      return <Textarea rows={3} maxLength={bound(form, "max_length") ?? FIELD_LONG_TEXT_MAX} value={text} required={required} onChange={(event) => { onChange(event.target.value); }} />;
    case "number":
    case "integer":
      return (
        <Input
          type="number"
          inputMode={form.field.type === "integer" ? "numeric" : "decimal"}
          step={form.field.type === "integer" ? 1 : "any"}
          min={bound(form, "min")}
          max={bound(form, "max")}
          value={text}
          required={required}
          onChange={(event) => { onChange(event.target.value); }}
        />
      );
    case "date":
      return <Input type="date" value={text} required={required} onChange={(event) => { onChange(event.target.value); }} />;
    case "datetime":
      return <Input type="datetime-local" value={text} required={required} onChange={(event) => { onChange(event.target.value); }} />;
    case "dropdown":
      return (
        <Select value={text} required={required} onChange={(event) => { onChange(event.target.value); }}>
          <option value="" />
          {optionsOf(form, language).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      );
    case "asset":
      return <AssetPicker organisationId={organisationId} value={text} label={label} onChange={onChange} />;
    case "multi_select":
      return <MultiChoice label={label} options={optionsOf(form, language)} value={isChoiceList(value) ? value : []} onChange={onChange} />;
    default:
      return <Input maxLength={bound(form, "max_length") ?? FIELD_TEXT_MAX} value={text} required={required} onChange={(event) => { onChange(event.target.value); }} />;
  }
}

function MultiChoice({ label, options, value, onChange }: { label: string; options: readonly Choice[]; value: readonly string[]; onChange: (value: string[]) => void }) {
  return (
    <fieldset aria-label={label} className="space-y-2">
      {options.map((option) => (
        <Checkbox
          key={option.value}
          label={option.label}
          checked={value.includes(option.value)}
          onCheckedChange={(checked) => {
            onChange(checked === true ? [...value, option.value] : value.filter((entry) => entry !== option.value));
          }}
        />
      ))}
    </fieldset>
  );
}

/** The custom fields of the chosen type (TKT-050), each shown while its condition holds; required ones are marked. */
export function TypeFields({ fields, values, problems, language, organisationId, onChange }: FieldsProps) {
  const { t } = useTranslation();
  const visible = fields.filter((form) => isFieldVisible(form.field, values));
  if (visible.length === 0) return null;
  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid="new-ticket-fields">
      {visible.map((form) => {
        const key = form.field.key;
        const label = labelOf(form, language);
        const required = form.field.staff_required && form.field.required_on === "create";
        const problem = problems[`field:${key}`];
        if (form.field.type === "bool") {
          return (
            <div key={key} className="sm:col-span-2">
              <Checkbox label={label} checked={values[key] === true} onCheckedChange={(checked) => { onChange(key, checked === true); }} />
            </div>
          );
        }
        return (
          <Field
            key={key}
            className={form.field.type === "long_text" ? "sm:col-span-2" : undefined}
            label={required ? <>{label} <span aria-hidden="true">*</span></> : label}
            error={problem ? t(problem.key) : undefined}
          >
            <Control form={form} value={values[key]} language={language} label={label} organisationId={organisationId ?? null} onChange={(value) => { onChange(key, value); }} />
          </Field>
        );
      })}
    </div>
  );
}
